import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db as defaultDb } from '../shared/api/firebase/config';
import { FS } from '../shared/constants/firestorePaths';
import { FIREBASE_CONFIG } from '../config/Config';

const defaultAppId = String(FIREBASE_CONFIG?.projectId || '').trim();

export async function fetchClientsByCpf(searchCpf, safeDocDigits, db = defaultDb, appId = defaultAppId) {
  if (!db || !appId) return [];
  const clientsRef = collection(db, ...FS.clients(appId));
  const queries = [];
  if (safeDocDigits.length === 11) {
    queries.push(getDocs(query(clientsRef, where('qualification.physical.cpf', '==', searchCpf), limit(5))));
    if (searchCpf !== safeDocDigits) {
      queries.push(getDocs(query(clientsRef, where('qualification.physical.cpf', '==', safeDocDigits), limit(5))));
    }
  }
  if (safeDocDigits.length === 14) {
    queries.push(getDocs(query(clientsRef, where('qualification.legal.cnpj', '==', searchCpf), limit(5))));
  }
  const snaps = await Promise.all(queries);
  // FIX ID-FANTASMA (21/08/2026): "id" precisa vir DEPOIS do spread de d.data().
  // Documentos legados podem ter um campo "id" gravado dentro do próprio corpo
  // (bug de escrita já corrigido em ClientManager.jsx) — se "id: d.id" vier
  // primeiro, o spread de dados por cima apaga o ID real do Firestore com o
  // valor (às vezes null) gravado no documento, quebrando a exclusão do
  // próprio cliente na varredura de duplicidade (H3).
  return snaps.flatMap((snap) => snap.docs.map((d) => ({ ...(d.data() || {}), id: d.id })));
}

export async function fetchClientsByName(strongestToken, db = defaultDb, appId = defaultAppId) {
  if (!db || !appId || !strongestToken) return [];
  const clientsRef = collection(db, ...FS.clients(appId));
  const snap = await getDocs(query(clientsRef, where('searchKeywords', 'array-contains', strongestToken), limit(10)));
  return snap.docs.map((d) => ({ ...(d.data() || {}), id: d.id }));
}

export async function fetchProcessesByName(strongestToken, db = defaultDb, appId = defaultAppId) {
  if (!db || !appId || !strongestToken) return [];
  const processesRef = collection(db, ...FS.processes(appId));
  const snap = await getDocs(query(processesRef, where('searchKeywords', 'array-contains', strongestToken), limit(20)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
}

// Fusão Processos/Caderno (MC25) — substitui fetchProcessesByName como fonte
// do scanner de conflito. `processes` está vazia em produção (confirmado na
// auditoria); o dado real de parte contrária vive em
// autos_workspaces/{id}.partes.adversas[] (array de {nome,tipo,documento}),
// não num campo `parteContraria` string + searchKeywords como em `processes`.
// Estratégia: buscar TODOS os workspaces com partes.adversas não vazio e
// deixar o casamento de nome (substring, normalizado) para o conflictScanner
// — mesma lógica que já existia, só troca a fonte. Full-table read é
// aceitável no volume atual (15 workspaces em 21/08/2026); se o volume
// crescer para centenas, isso precisa de índice dedicado — não fazer isso
// silenciosamente, revisar quando o caso surgir.
export async function fetchWorkspacesWithAdverseParties(db = defaultDb, appId = defaultAppId) {
  if (!db || !appId) return [];
  const workspacesRef = collection(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces');
  const snap = await getDocs(query(workspacesRef, limit(500)));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((w) => Array.isArray(w?.partes?.adversas) && w.partes.adversas.length > 0);
}
